import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useI18n } from "../i18n";
import { BUSINESS_MODELS } from "../config/business-models";
import { PLANS, startingPrice } from "../config/plans";
import Icon from "../components/ui/Icon";

const NAV = [
  { href: "#home", th: "หน้าแรก", en: "Home" },
  { href: "#services", th: "โมดูล", en: "Product" },
  { href: "#models", th: "โมเดลธุรกิจ", en: "Resource" },
  { href: "#pricing", th: "ราคา", en: "Price" },
  { href: "#footer", th: "ติดต่อ", en: "Contact" },
];

const ADVANTAGES = [
  { tone: "pink", icon: "bulb", th: "ใช้งานง่าย", en: "Usable To Easy", dth: "ออกแบบให้ทีมของคุณเริ่มใช้งานได้จริงตั้งแต่วันแรก ไม่ต้องอบรมนาน", den: "Built so your team is productive from day one, no long onboarding." },
  { tone: "blue", icon: "mobile", th: "ใช้ได้ทุกที่", en: "Used Anywhere", dth: "เป็นเว็บแอปที่ใช้งานได้จากทุกอุปกรณ์ ทุกที่ที่มีอินเทอร์เน็ต", den: "A web app that works on any device, anywhere with internet." },
  { tone: "lav", icon: "shield", th: "บริการที่ดีที่สุด", en: "Best Services", dth: "ทีมงานพร้อมช่วยเหลือและอัปเดตฟีเจอร์ใหม่ให้ธุรกิจคุณอยู่เสมอ", den: "Our team keeps helping and shipping features for your business." },
  { tone: "peach", icon: "eye", th: "โปร่งใสตรวจสอบได้", en: "Transparency", dth: "ทุกรายการเงินและกิจกรรมของทีมตรวจสอบย้อนหลังได้ทั้งหมด", den: "Every transaction and team activity is fully auditable." },
];

const SERVICES = [
  { icon: "users", th: "ดูแลลูกค้าครบวงจร", en: "Track Customers And Get The Full View Information", dth: "เก็บประวัติลูกค้า การซื้อ และแชทไว้ในโปรไฟล์เดียว ดูภาพรวมได้ครบในหน้าเดียว", den: "Keep history, purchases and chats in one profile with a full pipeline view.", graphic: "queue" },
  { icon: "search", th: "ค้นหาและจัดการลีด", en: "Help You Find The Right Leads With The Best Score", dth: "จัดลำดับลีดตามโอกาสปิดการขาย มอบหมายผู้ดูแลได้ทันที", den: "Score and rank leads by likelihood to close, assign owners instantly.", graphic: "pipeline" },
  { icon: "chartLine", th: "อินไซต์เชิงลึก", en: "Improve Your Process Based On Existing Data", dth: "เห็นรายได้ ยอดค้างชำระ และคิววันนี้แบบเรียลไทม์บนแดชบอร์ดเดียว", den: "Real-time revenue, outstanding balances and today's queue in one dashboard.", graphic: "chart" },
  { icon: "calendar", th: "จองคิวและนัดหมาย", en: "Ease Of Booking Appointments For Your Customers", dth: "ปฏิทินกลางแบบลากวาง ตั้งเตือนอัตโนมัติ ลดการลืมนัดและยกเลิกกะทันหัน", den: "A shared drag-and-drop calendar with auto-reminders that cut no-shows.", graphic: "calendar" },
  { icon: "card", th: "รับชำระเงินทุกช่องทาง", en: "Keep Connected Easily With Your Payment Flow", dth: "ออกบิลแล้วให้ลูกค้าสแกน QR PromptPay จ่ายได้ทันที ระบบเช็คสถานะให้เอง", den: "Issue a bill and let customers scan QR PromptPay — status verified automatically.", graphic: "qr" },
  { icon: "chat", th: "แชทรวมทุกช่องทาง", en: "Connect Every Channel Into One Inbox", dth: "รวมข้อความจากไลฟ์ แชท และแบบฟอร์มไว้กล่องเดียว ไม่พลาดลูกค้าคนไหน", den: "Merge live, chat and form messages into one inbox — never miss a lead.", graphic: "chat" },
];

const STATS = [
  { value: "65%", th: "ลดเวลาปิดงานเอกสาร", en: "Reduced Paperwork Time" },
  { value: "฿1.5-2M", th: "ประหยัดต้นทุนต่อปี", en: "Reduced Expenses" },
  { value: "50%", th: "ค้นหาข้อมูลลูกค้าเร็วขึ้น", en: "Reduced Search" },
  { value: "75%", th: "ลดจำนวนคลิกในการทำงาน", en: "Reduced Clicks" },
  { value: "50%", th: "เพิ่มประสิทธิภาพทีมงาน", en: "Increased Efficiency" },
  { value: "50%", th: "ลดเวลาปิดการขาย", en: "Reduced Time" },
];

const TESTIMONIALS = [
  {
    name: "คุณพิมพ์ชนก ภักดี",
    nameEn: "Pimchanok P.",
    role: "เจ้าของคลินิกความงาม",
    roleEn: "Aesthetic clinic owner",
    th: "เมื่อก่อนจดคิวในสมุด ลูกค้าลืมนัดบ่อยมาก พอย้ายมาใช้ระบบนี้ คิวไม่หลุดอีกเลย และรู้ทันทีว่าคอร์สลูกค้าเหลือกี่ครั้ง",
    en: "We used to track bookings on paper and customers kept forgetting. Now nothing slips, and we instantly see how many sessions are left.",
  },
  {
    name: "คุณธนกร รุ่งเรือง",
    nameEn: "Thanakorn R.",
    role: "ร้านเช่ามอเตอร์ไซค์",
    roleEn: "Motorbike rental",
    th: "ชอบตรงที่ดูได้ว่ารถคันไหนว่าง คันไหนถูกเช่าอยู่ แล้วออกสัญญาให้ลูกค้าเซ็นในจอได้เลย ไม่ต้องปริ้นท์",
    en: "I can see which bikes are free or rented, then have customers sign the contract on screen — no printing.",
  },
  {
    name: "คุณศิริพร จันทร์เพ็ญ",
    nameEn: "Siriporn J.",
    role: "แม่ค้าออนไลน์",
    roleEn: "Online seller",
    th: "ออเดอร์จากไลฟ์กับแชทเข้ามารวมที่เดียว ลูกค้าโอนแล้วส่งสลิป ระบบเช็คให้ ไม่ต้องไล่ดูยอดเองทีละคน",
    en: "Orders from live and chat land in one place. Customers send slips and the system checks them for me.",
  },
];


const PRICING_BENEFITS = [
  { icon: "chartBar", th: "เพิ่มยอดขาย", en: "Grow sales", sth: "จากการติดตามลูกค้าที่มีประสิทธิภาพ", sen: "with effective customer follow-up" },
  { icon: "users", th: "ลดภาระงานทีมขาย", en: "Lighten the sales team's load", sth: "ด้วยระบบอัตโนมัติ", sen: "with automation" },
  { icon: "heart", th: "สร้างความประทับใจให้ลูกค้า", en: "Impress your customers", sth: "ด้วยการสื่อสารที่ตรงใจ", sen: "with communication that lands" },
  { icon: "shield", th: "ข้อมูลปลอดภัย ได้มาตรฐาน", en: "Secure, compliant data", sth: "รองรับการเติบโตของธุรกิจ", sen: "ready for business growth" },
];

const FAQS = [
  {
    th: "ธุรกิจของฉันไม่ตรงกับ 18 โมเดล ใช้ได้ไหม?",
    en: "My business isn't one of the 18 models — can I still use it?",
    ath: "ใช้ได้ครับ โมเดลธุรกิจเป็นเพียงค่าตั้งต้นที่ช่วยเปิดโมดูลที่มักใช้คู่กัน คุณเปิด-ปิดโมดูลเองได้ทั้งหมดที่หน้าจัดการโมดูล",
    aen: "Yes. Business models are just presets that enable commonly paired modules — you can toggle every module yourself.",
  },
  {
    th: "รับชำระเงินได้ช่องทางไหนบ้าง?",
    en: "Which payment methods are supported?",
    ath: "รองรับ QR PromptPay ผ่านระบบชำระเงินจริง ใช้ได้ทั้งการจ่ายบิล มัดจำการจอง ตะกร้าสินค้า ขายหน้าร้าน และค่างวด รวมถึงรับแจ้งสลิปโอนเงินจากลูกค้า",
    aen: "QR PromptPay through a live payment gateway — for bills, booking deposits, carts, POS and installments, plus customer slip submissions.",
  },
  {
    th: "มีหลายสาขาต้องซื้อหลายระบบไหม?",
    en: "Do multiple branches need multiple systems?",
    ath: "ไม่ต้องครับ บัญชีเดียวจัดการได้หลายธุรกิจและหลายสาขา สลับดูข้อมูลรายสาขาหรือดูรวมทั้งหมดได้จากแถบด้านบน",
    aen: "No. One account manages multiple businesses and branches — switch or view everything from the top bar.",
  },
  {
    th: "ลูกค้าของฉันต้องโหลดแอปไหม?",
    en: "Do my customers need to install an app?",
    ath: "ไม่ต้องครับ ลูกค้าเปิดผ่านเว็บได้เลย ดูบิล จองคิว ดูสัญญา แจ้งโอนเงิน และแชทกับร้านได้จากหน้าเดียว",
    aen: "No. Customers use the web — bills, bookings, contracts, slip uploads and chat all in one place.",
  },
  {
    th: "ย้ายข้อมูลเดิมเข้าระบบได้ไหม?",
    en: "Can I migrate my existing data?",
    ath: "ได้ครับ ทีมงานช่วยนำเข้าข้อมูลลูกค้า รายการสินค้า และยอดค้างชำระให้ในช่วงเริ่มต้นใช้งาน",
    aen: "Yes — our team helps import customers, catalog and outstanding balances during onboarding.",
  },
];

const FOOTER = [
  {
    th: "บริษัท",
    en: "Company",
    links: [
      { th: "เกี่ยวกับเรา", en: "About" },
      { th: "ลูกค้าของเรา", en: "Candidate" },
      { th: "นโยบายความเป็นส่วนตัว", en: "Privacy Policy" },
      { th: "ร่วมงานกับเรา", en: "Careers" },
    ],
  },
  {
    th: "แพลตฟอร์ม",
    en: "Platform",
    links: [
      { th: "ภาพรวมระบบ", en: "Overview" },
      { th: "ชุมชนผู้ใช้", en: "Community" },
      { th: "หน่วยงานพันธมิตร", en: "Partners" },
      { th: "ภาษาที่รองรับ", en: "Language" },
    ],
  },
  {
    th: "บริการ",
    en: "Services",
    links: [
      { th: "CRM & ลูกค้า", en: "Campaigns" },
      { th: "สถิติการใช้งาน", en: "Statistics" },
      { th: "API สำหรับนักพัฒนา", en: "API Reference" },
      { th: "คู่มือเริ่มต้น", en: "Tutorials" },
    ],
  },
  {
    th: "โซเชียล",
    en: "Social",
    links: [
      { th: "Instagram", en: "Instagram" },
      { th: "LinkedIn", en: "LinkedIn" },
      { th: "Twitter / X", en: "Twitter" },
      { th: "Facebook", en: "Facebook" },
    ],
  },
];

export default function Landing() {
  const { isLoggedIn, isAdmin } = useApp();
  const { t, tx, pick } = useI18n();
  const [openFaq, setOpenFaq] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState("");

  if (isLoggedIn) return <Navigate to={isAdmin ? "/admin" : "/app"} replace />;

  return (
    <div className="lz-land" id="home">
      {/* ---------------- nav ---------------- */}
      <header className="lz-nav">
        <div className="lz-nav__inner">
          <Link to="/" className="lz-nav__brand">
            <span className="crm-logo">LU</span>
            LucidCRM
          </Link>
          <nav className="lz-nav__links">
            {NAV.map((item) => (
              <a key={item.href} href={item.href}>{tx(item.th, item.en)}</a>
            ))}
          </nav>
          <div className="lz-nav__actions">
            <Link to="/auth/login" className="lz-btn lz-btn--text">{t("action.login")}</Link>
            <Link to="/auth/register" className="lz-btn lz-btn--solid">{tx("เริ่มใช้งาน", "Get Started")}</Link>
          </div>
          <button type="button" className="lz-nav__burger" onClick={() => setNavOpen((v) => !v)} aria-label="menu">
            <Icon name={navOpen ? "close" : "menu"} />
          </button>
        </div>
        {navOpen ? (
          <div className="lz-nav__mobile">
            {NAV.map((item) => (
              <a key={item.href} href={item.href} onClick={() => setNavOpen(false)}>{tx(item.th, item.en)}</a>
            ))}
            <Link to="/auth/login" onClick={() => setNavOpen(false)}>{t("action.login")}</Link>
            <Link to="/auth/register" className="lz-btn lz-btn--solid" onClick={() => setNavOpen(false)}>{tx("เริ่มใช้งาน", "Get Started")}</Link>
          </div>
        ) : null}
      </header>

      {/* ---------------- hero ---------------- */}
      <section className="lz-hero">
        <div className="lz-hero__inner">
          <span className="lz-tag"><i /> {tx("ระบบบริหารธุรกิจ 18 โมเดล", "One system for 18 business models")}</span>
          <h1>
            {tx("จัดการลูกค้า ออเดอร์ และการเงิน", "Customers, orders and finance")}
            <br />
            <em>{tx("ให้เรียบง่ายในที่เดียว", "kept simple, in one place")}</em>
          </h1>
          <p>
            {tx(
              "LucidCRM รวมทุกโมดูลที่ธุรกิจคุณต้องใช้ไว้ในหน้าจอที่โล่ง อ่านง่าย เปิดใช้เฉพาะสิ่งที่จำเป็น",
              "LucidCRM puts every module your business needs on a calm, readable screen — enable only what you use."
            )}
          </p>
          <div className="lz-hero__cta">
            <Link to="/auth/register" className="lz-btn lz-btn--solid lz-btn--lg">{tx("เริ่มใช้งานฟรี", "Start free")}</Link>
            <a href="#services" className="lz-btn lz-btn--line lz-btn--lg">{tx("ดูโมดูลทั้งหมด", "See modules")}</a>
          </div>
        </div>

        {/* ภาพจำลองหน้าจอ — ใช้โครง icon rail + แผงเมนู เหมือนตัวระบบจริง */}
        <div className="lz-frame" aria-hidden="true">
          <div className="lz-frame__bar"><i /><i /><i /></div>
          <div className="lz-frame__body">
            <div className="lz-frame__rail">
              {["dashboard", "users", "chat", "calendar", "box", "money"].map((name, index) => (
                <span key={name} className={index === 0 ? "is-on" : ""}><Icon name={name} /></span>
              ))}
            </div>
            <div className="lz-frame__panel">
              <b />
              {[1, 2, 3, 4, 5].map((row) => (
                <span key={row} className={row === 1 ? "is-on" : ""} />
              ))}
            </div>
            <div className="lz-frame__main">
              <div className="lz-frame__kpis">
                {[["฿248K", tx("รายได้เดือนนี้", "Revenue")], ["1,284", tx("ลูกค้า", "Customers")], ["36", tx("นัดวันนี้", "Today")], ["฿42K", tx("ค้างชำระ", "Outstanding")]].map(([value, label]) => (
                  <div key={label}><strong>{value}</strong><small>{label}</small></div>
                ))}
              </div>
              <svg viewBox="0 0 400 110" preserveAspectRatio="none" className="lz-frame__chart">
                <polyline points="0,90 40,80 80,84 120,62 160,68 200,46 240,52 280,30 320,38 360,18 400,10" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- stats ---------------- */}
      <section className="lz-stats">
        {STATS.map((stat) => (
          <div key={stat.en}>
            <strong>{stat.value}</strong>
            <span>{tx(stat.th, stat.en)}</span>
          </div>
        ))}
      </section>

      {/* ---------------- advantages ---------------- */}
      <section className="lz-sec" id="advantages">
        <div className="lz-cols4">
          {ADVANTAGES.map((item) => (
            <div key={item.en} className="lz-point">
              <span className="lz-point__icon"><Icon name={item.icon} /></span>
              <h3>{tx(item.th, item.en)}</h3>
              <p>{tx(item.dth, item.den)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- services ---------------- */}
      <section className="lz-sec lz-sec--split" id="services">
        <div className="lz-sec__head">
          <span className="lz-eyebrow">{tx("โมดูล", "Modules")}</span>
          <h2>{tx("ทุกอย่างที่งานประจำวันต้องใช้", "Everything the daily work needs")}</h2>
          <p>{tx("เลือกเปิดเฉพาะโมดูลที่ธุรกิจคุณใช้ ที่เหลือไม่รกหน้าจอ", "Turn on only the modules you use — the rest stay out of the way.")}</p>
        </div>
        <ol className="lz-steps">
          {SERVICES.map((item, index) => (
            <li key={item.en}>
              <span className="lz-steps__no">{String(index + 1).padStart(2, "0")}</span>
              <span className="lz-steps__icon"><Icon name={item.icon} /></span>
              <div>
                <h3>{tx(item.th, item.en)}</h3>
                <p>{tx(item.dth, item.den)}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ---------------- models ---------------- */}
      <section className="lz-sec lz-sec--band" id="models">
        <div className="lz-sec__head lz-sec__head--center">
          <span className="lz-eyebrow">{tx("โมเดลธุรกิจ", "Business models")}</span>
          <h2>{tx("รองรับ 18 ประเภทธุรกิจ", "Built for 18 business types")}</h2>
          <p>{tx("เลือกโมเดลที่ใกล้เคียงธุรกิจคุณ ระบบเปิดโมดูลที่จำเป็นให้อัตโนมัติ", "Pick the closest model — required modules turn on automatically.")}</p>
        </div>
        <div className="lz-chips">
          {BUSINESS_MODELS.map((model) => (
            <span className="lz-chip" key={model.key}>
              <Icon name={model.icon} />
              {pick(model)}
            </span>
          ))}
        </div>
      </section>

      {/* ---------------- testimonials ---------------- */}
      <section className="lz-sec">
        <div className="lz-sec__head">
          <span className="lz-eyebrow">{tx("รีวิวจากลูกค้า", "Testimonials")}</span>
          <h2>{tx("เสียงจากผู้ใช้งานจริง", "Words from real users")}</h2>
        </div>
        <div className="lz-quotes">
          {TESTIMONIALS.map((item) => (
            <figure className="lz-quote" key={item.nameEn}>
              <blockquote>{tx(item.th, item.en)}</blockquote>
              <figcaption>
                <b>{tx(item.name, item.nameEn)}</b>
                <span>{tx(item.role, item.roleEn)}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ---------------- pricing ---------------- */}
      <section className="lz-sec lz-sec--band" id="pricing">
        <div className="lz-sec__head lz-sec__head--center">
          <span className="lz-eyebrow">{tx("ราคา", "Pricing")}</span>
          <h2>{tx("CRM & Social Media Management Packages", "CRM & Social Media Management Packages")}</h2>
          <p>
            {tx(
              "เปลี่ยนทุก Lead จาก Social Media ให้กลายเป็นโอกาสทางการขาย พร้อมระบบติดตามลูกค้าอัตโนมัติ ครบวงจร",
              "Turn every social media lead into a sales opportunity, with end-to-end automated follow-up."
            )}
          </p>
          <small className="lz-note">
            {tx("ราคายังไม่รวม VAT 7% · จ่ายรายปีถูกกว่า ฟรี 2 เดือน · ไม่มีค่าติดตั้งแอบแฝง", "Excludes 7% VAT · pay yearly and get 2 months free · no hidden setup fees.")}
          </small>
        </div>

        <div className="lz-plans">
          {PLANS.map((plan) => (
            <div className={`lz-plan ${plan.featured ? "is-featured" : ""}`} key={plan.key}>
              {plan.featured ? <span className="lz-plan__flag">{tx("ยอดนิยม", "Popular")}</span> : null}
              <div className="lz-plan__name">{tx(plan.name, plan.nameEn)}</div>
              {plan.tagline ? <div className="lz-plan__tagline">{tx(plan.tagline, plan.taglineEn)}</div> : null}
              {plan.quoteOnly ? (
                <div className="lz-plan__price">{tx("ตามใบเสนอราคา", "Custom quote")}</div>
              ) : (
                <div className="lz-plan__price">
                  ฿{(plan.perUser ? plan.seatPrice : startingPrice(plan.key)).toLocaleString()}
                  <small>/{plan.perUser ? tx("User/เดือน", "user/mo") : tx("เดือน", "mo")}</small>
                  {plan.platformFee ? (
                    <small style={{ display: "block" }}>
                      + {tx("ค่า Platform", "platform fee")} ฿{plan.platformFee.toLocaleString()}/{tx("เดือน", "mo")}
                    </small>
                  ) : null}
                </div>
              )}
              <div className="lz-plan__detail">{tx(plan.detail, plan.detailEn)}</div>
              <ul>
                {plan.highlights.map((item) => (
                  <li key={item.en}>
                    <Icon name="check" />
                    {tx(item.th, item.en)}
                  </li>
                ))}
              </ul>
              <Link to="/auth/register" className={`lz-btn lz-btn--block ${plan.featured ? "lz-btn--solid" : "lz-btn--line"}`}>
                {tx("เริ่มใช้งาน", "Get Started")}
              </Link>
            </div>
          ))}
        </div>

        <div className="lz-benefits">
          {PRICING_BENEFITS.map((item) => (
            <div key={item.en}>
              <span className="lz-point__icon"><Icon name={item.icon} /></span>
              <div>
                <b>{tx(item.th, item.en)}</b>
                <small>{tx(item.sth, item.sen)}</small>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- faq ---------------- */}
      <section className="lz-sec lz-sec--split" id="faq">
        <div className="lz-sec__head">
          <span className="lz-eyebrow">FAQ</span>
          <h2>{tx("คำถามที่พบบ่อย", "Frequently asked questions")}</h2>
          <p>{tx("รวมคำถามที่เจ้าของธุรกิจถามเรามากที่สุดก่อนเริ่มใช้งาน", "The questions owners ask us most before getting started.")}</p>
        </div>
        <div className="lz-faq">
          {FAQS.map((faq, index) => (
            <div className={`lz-faq__item ${openFaq === index ? "is-open" : ""}`} key={faq.en}>
              <button type="button" aria-expanded={openFaq === index} onClick={() => setOpenFaq(openFaq === index ? -1 : index)}>
                <span>{tx(faq.th, faq.en)}</span>
                <Icon name={openFaq === index ? "minus" : "plus"} />
              </button>
              {openFaq === index ? <p>{tx(faq.ath, faq.aen)}</p> : null}
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- cta ---------------- */}
      <section className="lz-cta">
        <div>
          <h2>{tx("มาร่วมใช้งานกับเรา", "Let's Join With Us")}</h2>
          <p>{tx("สมัครใช้งานฟรี ไม่ต้องผูกบัตรเครดิต พร้อมใช้งานได้ทันทีหลังสมัคร", "By joining and enjoying our service, you can run your business easier.")}</p>
        </div>
        <Link to="/auth/register" className="lz-btn lz-btn--invert lz-btn--lg">{tx("เริ่มใช้งานตอนนี้", "Join Now")}</Link>
      </section>

      {/* ---------------- footer ---------------- */}
      <footer className="lz-footer" id="footer">
        <div className="lz-footer__cols">
          {FOOTER.map((col) => (
            <div key={col.en}>
              <div className="lz-footer__title">{tx(col.th, col.en)}</div>
              {col.links.map((link) => (
                <a href="#home" key={link.en}>{tx(link.th, link.en)}</a>
              ))}
            </div>
          ))}
          <div>
            <div className="lz-footer__title">{tx("รับข่าวสาร", "Stay Updated")}</div>
            <p>{tx("รับข่าวสารและอัปเดตฟีเจอร์ใหม่จากเราทางอีเมล", "Get news and updates from us delivered to your inbox.")}</p>
            <form
              className="lz-subscribe"
              onSubmit={(event) => {
                event.preventDefault();
                setSubscribeEmail("");
              }}
            >
              <input
                type="email"
                placeholder={tx("อีเมลของคุณ", "Your email")}
                aria-label={tx("อีเมลของคุณ", "Your email")}
                value={subscribeEmail}
                onChange={(event) => setSubscribeEmail(event.target.value)}
                required
              />
              <button type="submit" className="lz-btn lz-btn--solid">{tx("สมัคร", "Subscribe")}</button>
            </form>
          </div>
        </div>
        <div className="lz-footer__bar">
          <span>© {new Date().getFullYear()} LucidCRM. {tx("สงวนลิขสิทธิ์", "All rights reserved")}</span>
          <span>{tx("นโยบายความเป็นส่วนตัว · ข้อกำหนดการใช้งาน", "Privacy Policy · Terms and Conditions")}</span>
        </div>
      </footer>
    </div>
  );
}
